import { useState, useEffect } from "react";
import { Download, Upload, Plus } from "lucide-react";
import { showToast } from "../../utils/toastNotification";
import { ConfirmModal } from "../../components/base/BaseModal";
import { useSchedule } from "../../hooks/useSchedule";
import MonthCalendar from "../../components/workSchedule/MonthCalendar";
import ScheduleList from "../../components/workSchedule/ScheduleList";
import WorkScheduleModal from "../../components/workSchedule/WorkScheduleModal";
import ImportPreviewModal from "../../components/workSchedule/ImportPreviewModal";
import { PermissionHidden } from "../../components/PermissionGuard";
import dayjs from "dayjs";
import { validateFileImport } from "../../validator/fileValidator";
import { downloadUtils } from "../../utils/downLoadUtils";
import { WORK_SCHEDULE_API } from "../../apis/workSchedule";
import { parseScheduleExcelFile } from "../../utils/excelImportUtils";

export default function WorkSchedule() {
    const {
        loading,
        error,
        selectedMonth,
        selectedYear,
        schedules,
        pagination,
        counts,
        fetchSchedulesPagination,
        importSchedule,
        deleteSchedule,
        hasScheduleForDay,
        updateStatus,
        formatDate,
        clearError,
        setSelectedMonth,
        setSelectedYear,
        getTemplate,
        createScheduleItem,
        updateScheduleItem,
        setCounts,
    } = useSchedule();

    const [deleteConfirm, setDeleteConfirm] = useState({
        isOpen: false,
        schedule: null,
    });

    const [modalState, setModalState] = useState({
        isOpen: false,
        mode: "create",
        data: null,
    });

    const [selectedDate, setSelectedDate] = useState(null);
    const [activeFilter, setActiveFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [isFetching, setIsFetching] = useState(false);
    const [importPreview, setImportPreview] = useState({
        isOpen: false,
        file: null,
        data: [],
        loading: false,
        isSubmitting: false,
    });

    const pageSize = 10;

    const fetchCounts = async (monthYear, date = null) => {
        try {
            const result = await WORK_SCHEDULE_API.countWorkSchedules(null, date ? null : monthYear, date);
            
            setCounts({
                all: result.total || 0,
                active: result.active || 0,
                inactive: result.inactive || 0
            });

            return result.total || 0;
        } catch (error) {
            return 0;
        }
    };

    const fetchAllSchedulesData = async (monthYear) => {
        if (isFetching) {
            return;
        }

        setIsFetching(true);

        try {
            const totalItems = await fetchCounts(monthYear);

            await fetchSchedulesPagination({
                monthYear,
                isActive: null,
                page: 1,
                size: pageSize
            });

            if (totalItems > pageSize) {
                await fetchSchedulesPagination({
                    monthYear,
                    isActive: null,
                    page: 1,
                    size: Math.max(totalItems, pageSize)
                });
            }
        } catch (error) {
            showToast.error("Lỗi khi tải dữ liệu lịch tiếp dân.");
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        const monthYear = `${selectedMonth}/${selectedYear}`;

        fetchAllSchedulesData(monthYear);

        setCurrentPage(1);
        setActiveFilter("all");
        setSelectedDate(null);
        // Tải lại dữ liệu khi đổi tháng/năm
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMonth, selectedYear]);

    useEffect(() => {
        return () => {
            if (error) {
                clearError();
            }
        };
    }, [error, clearError]);

    const handleDateSelect = async (date) => {
        setSelectedDate(date);
        setCurrentPage(1);

        const isActiveValue = activeFilter === "all" ? null : activeFilter === "active" ? true : false;

        if (date) {
            await Promise.all([
                fetchSchedulesPagination({
                    date: date,
                    isActive: isActiveValue,
                    page: 1,
                    size: pageSize
                }),
                fetchCounts(null, date)
            ]);
        } else {
            const monthYear = `${selectedMonth}/${selectedYear}`;
            await Promise.all([
                fetchSchedulesPagination({
                    monthYear,
                    isActive: isActiveValue,
                    page: 1,
                    size: pageSize
                }),
                fetchCounts(monthYear)
            ]);
        }
    };

    const handleMonthChange = (newMonth, forceRefresh = false) => {
        if (forceRefresh && newMonth === selectedMonth) {
            const monthYear = `${newMonth}/${selectedYear}`;
            setCurrentPage(1);
            setActiveFilter("all");
            setSelectedDate(null);
            fetchAllSchedulesData(monthYear);
        } else {
            setSelectedMonth(newMonth);
        }
    };

    const handleYearChange = (newYear, forceRefresh = false) => {
        if (forceRefresh && newYear === selectedYear) {
            const monthYear = `${selectedMonth}/${newYear}`;
            setCurrentPage(1);
            setActiveFilter("all");
            setSelectedDate(null);
            fetchAllSchedulesData(monthYear);
        } else {
            setSelectedYear(newYear);
        }
    };

    const handleEdit = (schedule) => {
        setModalState({
            isOpen: true,
            mode: "edit",
            data: schedule,
        });
    };

    const handleDelete = (schedule) => {
        setDeleteConfirm({ isOpen: true, schedule });
    };



    const handleDeleteConfirm = async () => {
        if (!deleteConfirm.schedule) return;

        try {
            const schedule = deleteConfirm.schedule;
            // Nếu lịch đang hoạt động (active), tự động tắt trạng thái trước khi gửi lệnh xóa tới Backend
            if (schedule.is_active === true || schedule.isActive === true) {
                await updateStatus(schedule);
            }

            const result = await deleteSchedule(schedule.id);
            if (result.success) {
                showToast.success("Đã xóa lịch tiếp dân thành công.");
                const monthYear = `${selectedMonth}/${selectedYear}`;
                if (selectedDate) {
                    await Promise.all([
                        fetchSchedulesPagination({
                            date: selectedDate,
                            isActive: null,
                            page: currentPage,
                            size: pageSize
                        }),
                        fetchCounts(null, selectedDate)
                    ]);
                } else {
                    await Promise.all([
                        fetchSchedulesPagination({
                            monthYear,
                            isActive: null,
                            page: currentPage,
                            size: pageSize
                        }),
                        fetchCounts(monthYear)
                    ]);
                }
            } else {
                showToast.error(result.error || "Xóa lịch tiếp dân thất bại.");
            }
        } catch (error) {
            showToast.error("Có lỗi xảy ra khi xóa lịch tiếp dân.");
        } finally {
            setDeleteConfirm({ isOpen: false, schedule: null });
        }
    };

    const handleDownload = async () => {
        try {
            const result = await getTemplate();
            if (result.success) {
                downloadUtils.handleDownloadExcel(result.data.data);
                showToast.success(
                    result.message || "Đã tải xuống template lịch tiếp dân."
                );
            } else {
                showToast.error(result.error || "Lấy template lịch tiếp dân thất bại.");
            }
        } catch (error) {
            showToast.error("Có lỗi xảy ra khi tải template lịch tiếp dân.");
        }
    };

    const handleImport = async () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".xlsx,.xls,.csv";
        input.onchange = async (event) => {
            const file = event.target.files[0];
            if (!file) return;

            const validation = await validateFileImport({ file });
            if (!validation.valid) {
                showToast.error(validation.errors.file);
                return;
            }

            try {
                setImportPreview({
                    isOpen: true,
                    file: file,
                    data: [],
                    loading: true,
                    isSubmitting: false,
                });

                const parsedData = await parseScheduleExcelFile(file);
                setImportPreview({
                    isOpen: true,
                    file: file,
                    data: parsedData,
                    loading: false,
                    isSubmitting: false,
                });
            } catch (error) {
                showToast.error(
                    "Không thể đọc dữ liệu từ file Excel: " + (error.message || "Lỗi định dạng")
                );
                setImportPreview({
                    isOpen: false,
                    file: null,
                    data: [],
                    loading: false,
                    isSubmitting: false,
                });
            }
        };
        input.click();
    };

    const handleConfirmImport = async () => {
        if (!importPreview.file) return;

        setImportPreview((prev) => ({ ...prev, isSubmitting: true }));
        try {
            const result = await importSchedule(importPreview.file);
            if (result.success) {
                showToast.success(result.data?.message || "Import lịch tiếp công dân thành công!");

                // Xác định tháng / năm của các lịch vừa import
                let targetMonth = selectedMonth;
                let targetYear = selectedYear;

                if (importPreview.data && importPreview.data.length > 0) {
                    const firstValid = importPreview.data.find(
                        (item) => item.isValid !== false && item.ngayTiepDan
                    );
                    if (firstValid) {
                        const parts = firstValid.ngayTiepDan.split("/");
                        if (parts.length === 3) {
                            const parsedM = parseInt(parts[1], 10);
                            const parsedY = parseInt(parts[2], 10);
                            if (!isNaN(parsedM) && !isNaN(parsedY)) {
                                targetMonth = parsedM;
                                targetYear = parsedY;
                            }
                        }
                    }
                }

                setImportPreview({
                    isOpen: false,
                    file: null,
                    data: [],
                    loading: false,
                    isSubmitting: false,
                });

                setCurrentPage(1);
                setActiveFilter("all");
                setSelectedDate(null);

                if (targetMonth !== selectedMonth || targetYear !== selectedYear) {
                    setSelectedMonth(targetMonth);
                    setSelectedYear(targetYear);
                } else {
                    const monthYear = `${targetMonth}/${targetYear}`;
                    await fetchAllSchedulesData(monthYear);
                }
            } else {
                showToast.error(
                    result.error ||
                    "Import lịch tiếp dân thất bại. Vui lòng kiểm tra định dạng file."
                );
                setImportPreview((prev) => ({ ...prev, isSubmitting: false }));
            }
        } catch (error) {
            showToast.error("Có lỗi xảy ra khi import. Vui lòng thử lại!");
            setImportPreview((prev) => ({ ...prev, isSubmitting: false }));
        }
    };

    const handleCloseImportPreview = () => {
        if (importPreview.isSubmitting) return;
        setImportPreview({
            isOpen: false,
            file: null,
            data: [],
            loading: false,
            isSubmitting: false,
        });
    };

    const handleAddSchedule = () => {
        setModalState({
            isOpen: true,
            mode: "create",
            data: null,
        });
    };

    const handleCloseModal = () => {
        setModalState({
            isOpen: false,
            mode: "create",
            data: null,
        });
    };

    const handleSubmitSchedule = async (scheduleData, mode) => {
        try {
            let result;

            if (mode === "create") {
                result = await createScheduleItem(scheduleData);
            } else {
                result = await updateScheduleItem(modalState.data.id, scheduleData);
            }

            if (result.success) {
                const monthYear = `${selectedMonth}/${selectedYear}`;
                const isActiveValue = activeFilter === "all" ? null : activeFilter === "active" ? true : false;

                showToast.success(
                    mode === "create"
                        ? "Tạo lịch tiếp dân thành công!"
                        : "Cập nhật lịch tiếp dân thành công!"
                );

                if (selectedDate) {
                    await Promise.all([
                        fetchSchedulesPagination({
                            date: selectedDate,
                            isActive: isActiveValue,
                            page: currentPage,
                            size: pageSize
                        }),
                        fetchCounts(monthYear)
                    ]);
                } else {
                    await Promise.all([
                        fetchSchedulesPagination({
                            monthYear,
                            isActive: isActiveValue,
                            page: currentPage,
                            size: pageSize
                        }),
                        fetchCounts(monthYear)
                    ]);
                }
                return true;
            } else {
                showToast.error(result.error || "Có lỗi xảy ra!");
                return false;
            }
        } catch (error) {
            showToast.error("Có lỗi xảy ra khi xử lý lịch tiếp dân!");
            return false;
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);

        const isActiveValue = activeFilter === "all" ? null : activeFilter === "active" ? true : false;

        if (selectedDate) {
            fetchSchedulesPagination({
                date: selectedDate,
                isActive: isActiveValue,
                page,
                size: pageSize
            });
        } else {
            fetchSchedulesPagination({
                monthYear: `${selectedMonth}/${selectedYear}`,
                isActive: isActiveValue,
                page,
                size: pageSize
            });
        }
    };

    const displaySchedules = schedules;

    const handleActiveFilterChange = async (filter) => {
        if (filter === activeFilter) return;

        setActiveFilter(filter);
        setCurrentPage(1);

        const isActiveValue = filter === "all" ? null : filter === "active" ? true : false;

        // Chỉ fetch danh sách, không fetch count vì count đã có sẵn
        if (selectedDate) {
            await fetchSchedulesPagination({
                date: selectedDate,
                isActive: isActiveValue,
                page: 1,
                size: pageSize
            });
        } else {
            const monthYear = `${selectedMonth}/${selectedYear}`;
            await fetchSchedulesPagination({
                monthYear,
                isActive: isActiveValue,
                page: 1,
                size: pageSize
            });
        }
    };

    const handleResetFilter = async () => {
        const monthYear = `${selectedMonth}/${selectedYear}`;
        setActiveFilter("all");
        setSelectedDate(null);
        setCurrentPage(1);

        await Promise.all([
            fetchSchedulesPagination({
                monthYear,
                isActive: null,
                page: 1,
                size: pageSize
            }),
            fetchCounts(monthYear)
        ]);

        showToast.success("Đã làm mới bộ lọc");
    };

    return (
        <div className="min-h-screen relative">
            <div className="mb-3 md:mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-1 md:mb-2">
                        Quản lý lịch tiếp dân
                    </h1>
                    <p className="text-sm md:text-base text-gray-600">
                        Lập lịch và quản lý lịch tiếp dân sắp tới của lãnh đạo
                    </p>
                </div>

                <div className="flex gap-2 md:gap-3 flex-wrap">
                    <PermissionHidden modulePrefix="LTD" action="CREATE">
                        <button
                            onClick={handleDownload}
                            className="px-3 md:px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm md:text-base rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center gap-2"
                        >
                            <Download className="w-4 h-4" />
                            <span className="hidden sm:inline">Tải file mẫu</span>
                            <span className="sm:hidden">File mẫu</span>
                        </button>
                    </PermissionHidden>
                    <PermissionHidden modulePrefix="LTD" action="CREATE">
                        <button
                            onClick={handleImport}
                            className="px-3 md:px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm md:text-base rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center gap-2"
                        >
                            <Upload className="w-4 h-4" />
                            <span className="hidden sm:inline">Nhập file</span>
                            <span className="sm:hidden">Nhập</span>
                        </button>
                    </PermissionHidden>
                    <PermissionHidden modulePrefix="LTD" action="CREATE">
                        <button
                            onClick={handleAddSchedule}
                            className="px-3 md:px-4 py-2 bg-blue-600 text-white text-sm md:text-base rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            <span className="hidden sm:inline">Thêm lịch</span>
                            <span className="sm:hidden">Thêm</span>
                        </button>
                    </PermissionHidden>
                </div>
            </div>


            <div className="flex flex-col lg:flex-row gap-3 md:gap-4">
                <div className="flex-1 order-2 lg:order-1">
                    <ScheduleList
                        schedules={displaySchedules}
                        loading={loading}
                        error={error}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        formatDate={formatDate}
                        selectedDate={selectedDate}
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        // canEdit={() => canUpdate('LTD')}
                        // canDelete={() => canDelete('LTD')}
                    />
                </div>

                <div className="w-full lg:w-96 order-1 lg:order-2">
                    <MonthCalendar
                        month={selectedMonth}
                        year={selectedYear}
                        onDateSelect={handleDateSelect}
                        onMonthChange={handleMonthChange}
                        onYearChange={handleYearChange}
                        selectedDate={selectedDate}
                        hasScheduleForDay={hasScheduleForDay}
                    />
                </div>
            </div>

            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, schedule: null })}
                onConfirm={handleDeleteConfirm}
                title="Xác nhận xóa"
                message={`Bạn có chắc chắn muốn xóa lịch tiếp dân ngày ${dayjs(
                    deleteConfirm.schedule?.ngay_tiep_dan
                ).format("DD/MM/YYYY")} ?`}
                confirmText="Xóa"
                cancelText="Hủy"
                type="danger"
            />

            <WorkScheduleModal
                isOpen={modalState.isOpen}
                onClose={handleCloseModal}
                onSubmit={handleSubmitSchedule}
                initialData={modalState.data}
                mode={modalState.mode}
            />

            <ImportPreviewModal
                isOpen={importPreview.isOpen}
                onClose={handleCloseImportPreview}
                onConfirm={handleConfirmImport}
                file={importPreview.file}
                previewData={importPreview.data}
                loading={importPreview.loading}
                isSubmitting={importPreview.isSubmitting}
            />
        </div>
    );
}
